import React, { useCallback, useMemo } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { CrossIcon } from '@modernloop/shared/icons';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  PushScheduleToAtsActionType,
  usePushScheduleToAtsDryRunQuery,
  usePushScheduleToAtsMutation,
} from 'src/generated/mloop-graphql';

import { useOrgAtsService } from 'src/hooks/atsService';

import PushScheduleToAtsUpdateDetails, { InterviewResult } from './PushScheduleToAtsUpdateDetails';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PUSH_SCHEDULE_TO_ATS_DRY_RUN_QUERY = gql`
  query PushScheduleToAtsDryRun($input: PushScheduleToAtsInput!) {
    pushScheduleToAtsDryRun(input: $input) {
      results {
        action
        interviewName
        updateDetails {
          timeChanged
          previousStartAt
          previousEndAt
          newStartAt
          newEndAt
          scorecardChanged
          previousAtsInterviewDefinition {
            atsId
            name
          }
          newAtsInterviewDefinition {
            atsId
            name
          }
          addedInterviewers {
            id
            fullName
          }
          removedInterviewers {
            id
            fullName
          }
        }
      }
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PUSH_SCHEDULE_TO_ATS_MUTATION = gql`
  mutation PushScheduleToAts($input: PushScheduleToAtsInput!) {
    pushScheduleToAts(input: $input) {
      results {
        action
        interviewName
      }
    }
  }
`;

type GroupedResults = {
  create: string[];
  update: InterviewResult[];
  cancel: string[];
  noOp: string[];
};

type Props = {
  taskId: string;
  onClose: () => void;
};

/**
 * Dialog that shows a preview of what the "Push schedule to ATS" action will do,
 * and allows the user to confirm the push.
 */
const PushScheduleToAtsDialog: React.FC<Props> = ({ taskId, onClose }) => {
  const { t } = useTranslation('hiring-dashboard-schedule');
  const { enqueueSnackbar } = useSnackbar();
  const atsService = useOrgAtsService();
  const atsName = atsService?.name || 'ATS';

  const { data, loading, error } = usePushScheduleToAtsDryRunQuery({
    variables: { input: { taskId } },
  });

  const [pushScheduleToAts, { loading: pushing }] = usePushScheduleToAtsMutation({
    variables: { input: { taskId } },
    onCompleted: () => {
      enqueueSnackbar(t('schedule.pushToAts.schedulePushed', { atsName }), { variant: 'success' });
      onClose();
    },
    onError: (err) => {
      enqueueSnackbar(err.message || t('schedule.pushToAts.failedToPush', { atsName }), { variant: 'error' });
    },
  });

  const handlePush = useCallback(() => {
    pushScheduleToAts();
  }, [pushScheduleToAts]);

  const groupedResults = useMemo((): GroupedResults => {
    const results = data?.pushScheduleToAtsDryRun?.results;
    if (!results) {
      return { create: [], update: [], cancel: [], noOp: [] };
    }

    return results.reduce(
      (acc, result) => {
        const name = result.interviewName || t('schedule.pushToAts.unknownInterview');
        switch (result.action) {
          case PushScheduleToAtsActionType.Create:
            acc.create.push(name);
            break;
          case PushScheduleToAtsActionType.Update:
            acc.update.push(result);
            break;
          case PushScheduleToAtsActionType.Cancel:
            acc.cancel.push(name);
            break;
          case PushScheduleToAtsActionType.NoOp:
          default:
            acc.noOp.push(name);
            break;
        }
        return acc;
      },
      { create: [], update: [], cancel: [], noOp: [] } as GroupedResults
    );
  }, [data, t]);

  const hasChanges =
    groupedResults.create.length > 0 || groupedResults.update.length > 0 || groupedResults.cancel.length > 0;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5">{t('schedule.pushToAts.pushScheduleTitle', { atsName })}</Typography>
          <IconButton onClick={onClose} size="small" disabled={pushing} data-testid="push-schedule-to-ats-dialog-close">
            <CrossIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={32} />
            <Typography variant="body1" color="text.secondary" mt={2}>
              {t('schedule.content.loadingPreview')}
            </Typography>
          </Stack>
        )}

        {error && (
          <Typography color="error" variant="body1">
            {error.message || t('schedule.content.failedToFetchPreview')}
          </Typography>
        )}

        {!loading && !error && data && (
          <Stack spacing={2}>
            {hasChanges ? (
              <>
                <Typography variant="body1">{t('schedule.pushToAts.changesWillBeMade', { atsName })}</Typography>
                <Stack spacing={3}>
                  {groupedResults.create.length > 0 && (
                    <Stack spacing={0.5}>
                      <Typography variant="body1" fontWeight={600}>
                        {t('schedule.pushToAts.willBeCreated')}
                      </Typography>
                      <Stack spacing={0.25} pl={2}>
                        {groupedResults.create.map((name, index) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Typography key={index} variant="body1" color="text.secondary">
                            • {name}
                          </Typography>
                        ))}
                      </Stack>
                    </Stack>
                  )}
                  {groupedResults.update.length > 0 && (
                    <Stack spacing={0.5}>
                      <Typography variant="body1" fontWeight={600}>
                        {t('schedule.pushToAts.willBeUpdated')}
                      </Typography>
                      <Stack spacing={1.5} pl={2}>
                        {groupedResults.update.map((result, index) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Stack key={index} spacing={0}>
                            <Typography variant="body1" color="text.secondary">
                              • {result.interviewName || t('schedule.pushToAts.unknownInterview')}
                            </Typography>
                            <PushScheduleToAtsUpdateDetails updateDetails={result.updateDetails} />
                          </Stack>
                        ))}
                      </Stack>
                    </Stack>
                  )}
                  {groupedResults.cancel.length > 0 && (
                    <Stack spacing={0.5}>
                      <Typography variant="body1" fontWeight={600}>
                        {t('schedule.pushToAts.willBeCanceled')}
                      </Typography>
                      <Stack spacing={0.25} pl={2}>
                        {groupedResults.cancel.map((name, index) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Typography key={index} variant="body1" color="text.secondary">
                            • {name}
                          </Typography>
                        ))}
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </>
            ) : (
              <Typography variant="body1" color="text.secondary">
                {t('schedule.pushToAts.noChanges', { atsName })}
              </Typography>
            )}
          </Stack>
        )}

        {!loading && !error && hasChanges && (
          <Typography variant="body2" color="text.secondary" mt={3}>
            {t('schedule.pushToAts.syncNote', { atsName })}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pushing}>
          {t('hiring-dashboard-common:common.cancel')}
        </Button>
        <Button
          onClick={handlePush}
          disabled={loading || pushing || !!error || !hasChanges}
          color="primary"
          variant="contained"
        >
          {pushing ? <CircularProgress size={20} /> : t('schedule.pushToAts.pushTo', { atsName })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PushScheduleToAtsDialog;
